/**
 * CustomerDraftsPage — list of mail drafts the citizen has created (and not
 * yet dropped off / discarded).
 *
 * Wired endpoints (citizen-scoped — `CitizenDraftController` on service-mail):
 *   - GET    /v1/drafts/me                via listMyDrafts()
 *   - DELETE /v1/drafts/me/:id            via discardMyDraft()
 *   - POST   /v1/drafts/me/:id/resume     via resumeMyDraft()
 *
 * The server filters rows by the JWT `customerId` claim; cross-customer
 * access returns 404 by design (no existence leak). The previous
 * operator-scoped wiring (`/v1/drafts`) 401'd for citizens — replaced here.
 */
import { useMemo, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Clock, Loader2, Package, Plus, Trash2 } from 'lucide-react';

import { ApiError } from '../../lib/api-client';
import {
  discardMyDraft,
  listMyDrafts,
  type DraftResponse,
  type ListDraftsResponse,
} from '../../lib/api/mail-drafts';
import { useCustomerAuth } from '../../lib/auth/useCustomerAuth';
import { PageHeader } from './PortalUI';

export const customerDraftsQueryKey = ['customer-drafts'] as const;

function StatusPill({ status }: { readonly status: DraftResponse['status'] }): JSX.Element {
  const map: Record<DraftResponse['status'], { readonly cls: string; readonly label: string }> = {
    DRAFT: { cls: 'bg-success-light text-success', label: 'Active' },
    SUBMITTED: { cls: 'bg-blue-50 text-blue-700', label: 'Submitted' },
    EXPIRED: { cls: 'bg-gray-100 text-gray-600', label: 'Expired' },
    DISCARDED: { cls: 'bg-gray-100 text-gray-600', label: 'Discarded' },
  };
  const m = map[status];
  return <span className={`inline-block px-2 py-0.5 text-[10px] font-bold rounded ${m.cls}`}>{m.label}</span>;
}

function hoursUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms)) return null;
  return Math.round(ms / 3_600_000);
}

export function CustomerDraftsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { token } = useCustomerAuth();

  const query = useQuery<ListDraftsResponse, Error>({
    queryKey: customerDraftsQueryKey,
    queryFn: () => listMyDrafts({ pageSize: 50 }, { bearerToken: token ?? undefined }),
    enabled: token !== null,
    retry: false,
    staleTime: 30_000,
  });

  const discardMutation = useMutation<DraftResponse, Error, string>({
    mutationFn: (id) => discardMyDraft(id, { bearerToken: token ?? undefined }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: customerDraftsQueryKey }),
  });

  const items = query.data?.items ?? [];
  const stats = useMemo(() => {
    const active = items.filter((d) => d.status === 'DRAFT');
    const expiring = active.filter((d) => {
      const left = hoursUntil(d.expiresAt);
      return left !== null && left <= 24;
    });
    const submitted = items.filter((d) => d.status === 'SUBMITTED');
    return { total: items.length, active: active.length, expiring: expiring.length, submitted: submitted.length };
  }, [items]);

  const errorMessage = (() => {
    if (!query.isError) return null;
    if (query.error instanceof ApiError && (query.error.status === 401 || query.error.status === 403)) {
      return 'Your session has expired. Sign in again to see your drafts.';
    }
    return query.error.message;
  })();

  return (
    <div>
      <PageHeader
        title="My Drafts"
        description="Pre-registered parcels waiting for outlet drop-off."
        actions={
          <Link
            to="/portal/parcel-booking"
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-nipost-green text-white rounded-lg text-sm font-medium hover:bg-nipost-green-dark"
          >
            <Plus className="w-4 h-4" /> New booking
          </Link>
        }
      />

      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-white border border-border rounded-xl p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-nipost-green-light text-nipost-green flex items-center justify-center">
            <Package className="w-4 h-4" />
          </div>
          <div>
            <p className="text-base font-bold text-gray-900 leading-none">{stats.total}</p>
            <p className="text-[10px] text-muted uppercase tracking-wider mt-0.5">Total drafts</p>
          </div>
        </div>
        <div className="bg-white border border-border rounded-xl p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center">
            <Clock className="w-4 h-4" />
          </div>
          <div>
            <p className="text-base font-bold text-gray-900 leading-none">{stats.expiring}</p>
            <p className="text-[10px] text-muted uppercase tracking-wider mt-0.5">Expiring &lt; 24h</p>
          </div>
        </div>
        <div className="bg-white border border-border rounded-xl p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center">
            <Package className="w-4 h-4" />
          </div>
          <div>
            <p className="text-base font-bold text-gray-900 leading-none">{stats.submitted}</p>
            <p className="text-[10px] text-muted uppercase tracking-wider mt-0.5">Submitted</p>
          </div>
        </div>
      </div>

      {errorMessage ? (
        <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-start gap-2" role="alert">
          <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-900">{errorMessage}</p>
        </div>
      ) : null}

      {query.isLoading ? (
        <div className="bg-white border border-border rounded-xl p-12 text-center text-sm text-muted flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading drafts…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-border rounded-xl p-12 text-center">
          <Package className="w-10 h-10 text-muted mx-auto mb-3" />
          <p className="text-sm text-gray-700 font-semibold mb-1">No drafts yet</p>
          <p className="text-xs text-muted mb-4">
            Start a parcel booking and a draft will appear here for outlet drop-off.
          </p>
          <Link
            to="/portal/parcel-booking"
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-nipost-green text-white rounded-lg text-sm font-medium hover:bg-nipost-green-dark"
          >
            <Plus className="w-4 h-4" /> New booking
          </Link>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] text-muted uppercase tracking-wider">
              <tr>
                <th className="p-3 text-left">Draft id</th>
                <th className="p-3 text-left">Receiver</th>
                <th className="p-3 text-left">Service</th>
                <th className="p-3 text-left">Created</th>
                <th className="p-3 text-left">Expires</th>
                <th className="p-3 text-center">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => {
                const left = hoursUntil(d.expiresAt);
                return (
                  <tr key={d.id} className="border-t border-border hover:bg-gray-50">
                    <td className="p-3 font-mono text-xs">{d.id.slice(0, 12)}</td>
                    <td className="p-3 text-sm">{d.payload.receiver?.name ?? '—'}</td>
                    <td className="p-3 text-xs font-mono">{d.payload.serviceClass ?? '—'}</td>
                    <td className="p-3 text-xs">{new Date(d.createdAt).toLocaleString()}</td>
                    <td className="p-3 text-xs">
                      {d.status === 'DRAFT' && left !== null && left <= 24 ? (
                        <span className="inline-flex items-center gap-1 text-amber-700">
                          <AlertTriangle className="w-3 h-3" /> {Math.max(0, left)}h left
                        </span>
                      ) : d.expiresAt ? (
                        new Date(d.expiresAt).toLocaleDateString()
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="p-3 text-center">
                      <StatusPill status={d.status} />
                    </td>
                    <td className="p-3 text-right">
                      {d.status === 'DRAFT' ? (
                        <button
                          type="button"
                          onClick={() => discardMutation.mutate(d.id)}
                          disabled={discardMutation.isPending}
                          className="inline-flex items-center gap-1 text-xs text-gray-500 font-semibold hover:text-danger disabled:opacity-50"
                          aria-label={`Discard draft ${d.id}`}
                        >
                          <Trash2 className="w-3 h-3" /> Discard
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
